import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import RelatorioFinanceiroTable, {
  RelatorioItem,
} from "../../components/financeiro/RelatorioFinanceiroTable";
import RelatorioFiltros, {
  FiltrosRelatorio,
} from "../../components/financeiro/RelatorioFiltros";
import Paginacao from "../../components/financeiro/Paginacao";
import { authService } from "../../services/auth";
import { relatoriosService } from "../../services/relatorios";
import { User } from "../../types/auth";
import {
  RelatorioPagamentoAssinante,
  RelatorioPagamentoFranquia,
} from "../../types/relatorios";
import { exportRelatorioToCSV } from "../../utils/exportCsv";
import { useNavigate } from "react-router-dom";

const RelatoriosFinanceiros: React.FC = () => {

  const mapAssinantes = (
    response: {
      results: RelatorioPagamentoAssinante[];
    }
  ): RelatorioItem[] => {
    return response.results.map(item => ({
      id: item.id,
      franquia_id: item.dados_franquia?.id,
      franqueado:
        item.dados_franquia?.nome_fantasia ?? item.franqueado,
      cliente: item.assinante,
      plano: item.plano,
      valorPago: Number(item.valor_pago),
      dataPagamento: item.data_pagamento,
      formaPagamento: item.forma,
      status:
        item.status === "PAGO"
          ? "Pago"
          : item.status === "PENDENTE"
          ? "Pendente"
          : null,
    }));
  };

  /* =======================
     STATES
  ======================= */
  const [profile, setProfile] = useState<User | null>(null);
  const [dados, setDados] = useState<RelatorioItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  const handleVoltar = async () => {
    // ===== FRANQUIA =====
    if (profile?.tipo === "FRQ") {
      navigate("/financeiro/franqueado/dashboard");
      return;
    }

    // ===== TACT / ADMIN =====
    if (viewMode === "DETALHE_FRANQUIA") {
      // volta para lista de franquias
      setViewMode("RESUMO_FRANQUIAS");
      setFranquiaSelecionada(null);
      setFranquiaIdSelecionada(null);
      setFiltros({});
      await carregarResumoFranquias(1);
      return;
    }

    // ===== TACT vendo franquias =====
    navigate("/financeiro/franqueado/dashboard");
  };

  const handleSalvarEdicao = async (
    id: number,
    payload: {
      status: "Pago" | "Pendente";
      forma_pagamento: string;
    }
  ) => {
    try {

      // 👉 TACT editando pagamento da FRANQUIA (resumo)
      if (profile?.tipo === "TACT" && viewMode === "RESUMO_FRANQUIAS") {
        await relatoriosService.patchPagamentoFranquia(id, payload);
      } 
      else {
        await relatoriosService.patchPagamentoAssinante(id, payload);
      }

      // ===== FRANQUEADO =====
      if (profile?.tipo === "FRQ") {
        const response =
          await relatoriosService.getRelatorioPagamentosAssinante(
            page,
            filtros
          );

        setDados(mapAssinantes(response));
        return;
      }

      // ===== TACT vendo assinantes de uma franquia =====
      if (profile?.tipo === "TACT" && viewMode === "DETALHE_FRANQUIA" && franquiaIdSelecionada) {
        const response =
          await relatoriosService.getRelatorioAssinantesPorFranquia(
            franquiaIdSelecionada,
            page,
            filtros
          );

        setDados(mapAssinantes(response));
        return;
      }

      // ===== TACT vendo resumo das franquias =====
      if (profile?.tipo === "TACT" && viewMode === "RESUMO_FRANQUIAS") {
        await carregarResumoFranquias(page);
      }

    } catch (error) {
      // Mensagem de log corrigida: esta função carrega o relatório de
      // pagamentos, não "salva edição" — o console.error anterior citava a
      // ação errada.
      console.error("Erro ao carregar relatório de pagamentos:", error);
      toast.error("Erro ao carregar o relatório de pagamentos.");
    }
  };


  const [viewMode, setViewMode] =
    useState<"RESUMO_FRANQUIAS" | "DETALHE_FRANQUIA">(
      "RESUMO_FRANQUIAS"
    );

  const [franquiaSelecionada, setFranquiaSelecionada] =
    useState<string | null>(null);

  const [franquiaIdSelecionada, setFranquiaIdSelecionada] =
    useState<number | null>(null);

  const [filtros, setFiltros] = useState<FiltrosRelatorio>({});

  useEffect(() => {
    if (!profile) return;

    // 🚫 FRANQUIA NÃO pode chamar por-franquia
    if (profile.tipo === "FRQ") {
      relatoriosService
        .getRelatorioPagamentosAssinante(1, filtros)
        .then(response => {
          setDados(
            response.results.map(item => ({
              id: item.id,
              franquia_id: item.dados_franquia?.id,
              franqueado:
                item.dados_franquia?.nome_fantasia ?? item.franqueado,
              cliente: item.assinante,
              plano: item.plano,
              valorPago: Number(item.valor_pago),
              dataPagamento: item.data_pagamento,
              formaPagamento: item.forma,
              status:
                item.status === "PAGO"
                  ? "Pago"
                  : item.status === "PENDENTE"
                  ? "Pendente"
                  : null,
            }))
          );
        });

      return;
    }

    // ✅ TACT
    if (viewMode === "RESUMO_FRANQUIAS") {
      carregarResumoFranquias(1, filtros);
    }

    if (
      viewMode === "DETALHE_FRANQUIA" &&
      franquiaIdSelecionada
    ) {
      relatoriosService
        .getRelatorioAssinantesPorFranquia(
          franquiaIdSelecionada,
          1,
          filtros
        )
        .then(response => {
          setDados(
            response.results.map(item => ({
              id: item.id,
              franquia_id: item.dados_franquia?.id,
              franqueado:
                item.dados_franquia?.nome_fantasia ?? item.franqueado,
              cliente: item.assinante,
              plano: item.plano,
              valorPago: Number(item.valor_pago),
              dataPagamento: item.data_pagamento,
              formaPagamento: item.forma,
              status:
                item.status === "PAGO"
                  ? "Pago"
                  : "Pendente",
            }))
          );
        });
    }
  }, [filtros]);




  /* =======================
     EXPORT CSV
  ======================= */
  const handleExportCSV = () => {
    const nomeArquivo =
      viewMode === "RESUMO_FRANQUIAS"
        ? "relatorio-franquias.csv"
        : "relatorio-assinantes.csv";

    exportRelatorioToCSV(dados, nomeArquivo);
  };

  /* =======================
     LOAD RESUMO FRANQUIAS
  ======================= */
  const carregarResumoFranquias = async (
    pagina = 1,
    filtrosAtivos = filtros
  ) => {
    setLoading(true);
    try {
      const response =
        await relatoriosService.getRelatorioPagamentosFranquia(
          pagina,
          filtrosAtivos
        );

      setDados(
        response.results.map(
          (item: RelatorioPagamentoFranquia): RelatorioItem => ({
            id: item.id,
            franquia_id: item.dados_franquia?.id,
            franqueado:
              item.dados_franquia?.nome_fantasia ?? item.franqueado,
            cliente: "-",
            plano: "-",
            valorPago: Number(item.valor_pago),
            dataPagamento: item.data_pagamento,
            formaPagamento: item.forma,
            status:
              item.status === "PAGO"
                ? "Pago"
                : item.status === "PENDENTE"
                ? "Pendente"
                : null,
          })
        )
      );

      setTotalPages(
        Math.ceil(response.count / response.results.length)
      );
      setPage(pagina);
    } catch (error) {
      console.error("Erro ao carregar franquias:", error);
      toast.error("Erro ao carregar o resumo de franquias.");
    } finally {
      setLoading(false);
    }
  };


  /* =======================
     LOAD INICIAL
  ======================= */
  useEffect(() => {
    async function loadInicial() {
      setLoading(true);
      try {
        const profileData = await authService.getProfile();
        setProfile(profileData);

        // ===== FRANQUIA =====
        if (profileData.tipo === "FRQ") {
          const response =
            await relatoriosService.getRelatorioPagamentosAssinante(1);

          setDados(
            response.results.map(item => ({
              id: item.id,
              franquia_id: item.dados_franquia?.id,
              franqueado:
                item.dados_franquia?.nome_fantasia ?? item.franqueado,
              cliente: item.assinante,
              plano: item.plano,
              valorPago: Number(item.valor_pago),
              dataPagamento: item.data_pagamento,
              formaPagamento: item.forma,
              status:
                item.status === "PAGO"
                  ? "Pago"
                  : item.status === "PENDENTE"
                  ? "Pendente"
                  : null,
            }))
          );

          setViewMode("DETALHE_FRANQUIA");
          setFranquiaSelecionada(
            profileData.franquia_membership?.franquia_nome ?? null
          );
          setFranquiaIdSelecionada(
            profileData.franquia_membership?.franquia_id ?? null
          );
          setTotalPages(
            Math.ceil(response.count / response.results.length)
          );
          setPage(1);
          return;
        }

        // ===== ADMIN / TACT =====
        await carregarResumoFranquias(1);
      } catch (error) {
        console.error("Erro ao carregar relatório:", error);
        toast.error("Erro ao carregar o relatório financeiro.");
      } finally {
        setLoading(false); // 🔥 ESSENCIAL
      }
    }

    loadInicial();
  }, []);


  /* =======================
     SELEÇÃO DE FRANQUIA
  ======================= */
  const carregarAssinantesDaFranquia = async (
    franquiaId: number,
    franquiaNome: string
  ) => {
    setLoading(true);
    setFranquiaIdSelecionada(franquiaId);

    if (profile?.tipo !== "TACT") return; 

    try {
      const response =
        await relatoriosService.getRelatorioAssinantesPorFranquia(
          franquiaId,
          1
        );

      setDados(
        response.results.map(
          (item: RelatorioPagamentoAssinante): RelatorioItem => ({
            id: item.id,
            franquia_id: item.dados_franquia?.id,
            franqueado:
              item.dados_franquia?.nome_fantasia ?? item.franqueado,
            cliente: item.assinante,
            plano: item.plano,
            valorPago: Number(item.valor_pago),
            dataPagamento: item.data_pagamento,
            formaPagamento: item.forma,
            status:
              item.status === "PAGO"
                ? "Pago"
                : item.status === "PENDENTE"
                ? "Pendente"
                : null,
          })
        )
      );

      setViewMode("DETALHE_FRANQUIA");
      setFranquiaSelecionada(franquiaNome);
      setTotalPages(
        Math.ceil(response.count / response.results.length)
      );
      setPage(1);
      setFiltros({});
    } catch (error) {
      console.error("Erro ao carregar assinantes:", error);
      toast.error("Erro ao carregar os assinantes da franquia.");
    } finally {
      setLoading(false);
    }
  };

  /* =======================
     RENDER
  ======================= */
  if (loading) {
    return <p className="text-gray-500">Carregando relatório...</p>;
  }

  if (!profile) {
    return <p className="text-red-500">Erro ao carregar perfil</p>;
  }

  return (
    <div className="space-y-6">
    <div className="flex justify-between items-center">
      <button
        onClick={handleVoltar}
        className="
          flex items-center gap-2
          rounded-md px-5 py-2
          bg-gray-900 text-white
          font-semibold text-sm
          shadow-md
          hover:bg-gray-800 hover:shadow-lg
          active:scale-95
          transition-all duration-200
        "
      >
        <span className="text-lg">←</span>
        Voltar
      </button>



      <button
        onClick={handleExportCSV}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
      >
        Exportar CSV
      </button>
    </div>


      <RelatorioFiltros
        dados={dados}
        tipoUsuario={profile.tipo === "FRQ" ? "FRANQUIA" : "ADMIN"}
        viewMode={viewMode}
        filtros={filtros}
        onChange={setFiltros}
      />

      <RelatorioFinanceiroTable
        dados={dados}
        tipoUsuario={profile.tipo === "FRQ" ? "FRANQUIA" : "ADMIN"}
        viewMode={viewMode}
        franquiaSelecionada={franquiaSelecionada}
        onSelectFranquia={carregarAssinantesDaFranquia}
        onSalvarEdicao={handleSalvarEdicao}
      />

      <Paginacao
        page={page}
        totalPages={totalPages}
        onPageChange={pagina => {
          if (viewMode === "RESUMO_FRANQUIAS") {
            carregarResumoFranquias(pagina);
          }
        }}
      />
    </div>
  );
};

export default RelatoriosFinanceiros;
